/**
 * Per-page JSON-LD compositions. These helpers return arrays of
 * schema objects (or null entries) ready to feed into Page's
 * `structuredData` prop, which renders one `<script
 * type="application/ld+json">` per non-null entry.
 *
 * Pages should compose by calling one of the helpers below rather than
 * importing individual schema builders, so the page surface stays
 * small.
 */

import {
  resolveBreadcrumbs,
  type BreadcrumbOverride,
} from "./breadcrumbs";
import type { GlobalSeoInput, SeoInput } from "./metadata";
import {
  articleSchema,
  breadcrumbSchema,
  collectionPageSchema,
  faqSchema,
  reportSchema,
  softwareApplicationSchema,
  videoSchema,
  webPageSchema,
  type ShareImageContext,
} from "./structured-data";

interface MarketingPageInput {
  pagePath: string;
  title: string;
  description?: string | null;
  image?: string | null;
  /** Optional Tina `faqs` block; emits `FAQPage` when populated. */
  faqs?: unknown;
  /** SEO breadcrumb list. Replaces the trail after Binarly when any label is set. */
  breadcrumbs?: BreadcrumbOverride[] | null;
}

export function marketingPageSchemas({
  pagePath,
  title,
  description,
  image,
  faqs,
  breadcrumbs,
}: MarketingPageInput) {
  return [
    webPageSchema({
      url: pagePath,
      name: title,
      description,
      image,
    }),
    breadcrumbSchema(resolveBreadcrumbs({ pagePath, overrides: breadcrumbs })),
    faqSchema(faqs as any),
  ];
}

interface ListingPageInput {
  pagePath: string;
  title: string;
  description?: string | null;
  items?: Array<{
    title?: string | null;
    templateType?: string | null;
    filename?: string | null;
  }>;
  breadcrumbs?: BreadcrumbOverride[] | null;
}

export function listingPageSchemas({
  pagePath,
  title,
  description,
  items,
  breadcrumbs,
}: ListingPageInput) {
  return [
    collectionPageSchema({
      url: pagePath,
      name: title,
      description,
      items,
    }),
    breadcrumbSchema(resolveBreadcrumbs({ pagePath, overrides: breadcrumbs })),
  ];
}

interface ResourceDetailInput {
  pagePath: string;
  resource: any;
  parentSeo?: SeoInput | null;
  globalSeo?: GlobalSeoInput | null;
  breadcrumbs?: BreadcrumbOverride[] | null;
}

/**
 * Picks the right schema for a resource detail page based on the
 * Tina `templateType`. Returns the resource schema + a BreadcrumbList.
 */
export function resourceDetailSchemas({
  pagePath,
  resource,
  parentSeo,
  globalSeo,
  breadcrumbs,
}: ResourceDetailInput) {
  if (resource?.published !== true) {
    return [];
  }
  const templateType = resource?.templateType as string | undefined;
  const shareImageContext: ShareImageContext = { parentSeo, globalSeo };

  let primary: Record<string, unknown> | null = null;
  switch (templateType) {
    case "video":
      primary = videoSchema({ resource, pagePath, shareImageContext });
      break;
    case "tool":
      primary = softwareApplicationSchema({
        resource,
        pagePath,
        shareImageContext,
      });
      break;
    case "report":
      primary = reportSchema({ resource, pagePath, shareImageContext });
      break;
    default:
      primary = articleSchema({ resource, pagePath, shareImageContext });
      break;
  }

  return [
    primary,
    breadcrumbSchema(
      resolveBreadcrumbs({
        pagePath,
        overrides: breadcrumbs ?? resource?.resourceSeo?.breadcrumbs,
      })
    ),
  ];
}

interface StandaloneReportInput {
  pagePath: string;
  resource: any;
  parentSeo?: SeoInput | null;
  globalSeo?: GlobalSeoInput | null;
  breadcrumbs?: BreadcrumbOverride[] | null;
}

/**
 * Schema for the dedicated `/reports/[slug]` collection (separate from
 * the resources `report` template).
 */
export function standaloneReportSchemas({
  pagePath,
  resource,
  parentSeo,
  globalSeo,
  breadcrumbs,
}: StandaloneReportInput) {
  return [
    reportSchema({
      resource,
      pagePath,
      shareImageContext: { parentSeo, globalSeo },
    }),
    breadcrumbSchema(
      resolveBreadcrumbs({
        pagePath,
        overrides: breadcrumbs ?? resource?.seo?.breadcrumbs,
      })
    ),
  ];
}
